import React, { useRef, useEffect, useCallback } from 'react';
import { useTabs } from '../../context/TabsContext';
import SprintDetail from '../SprintDetail';
import DocsArea from '../DocsArea';
import LogoLoader from '../ui/LogoLoader';
import OrphanWarningBanner from './OrphanWarningBanner';
import NewTabPage from './NewTabPage';
import CampaignsArea from '../CampaignsArea';
import { isTrulyOrphaned } from '../Sidebar/utils/orphanDetection';
import { detailContentGate } from './detailContentGate';

/**
 * Placeholder toolbar skeleton shown during tab transitions.
 * Keeps the toolbar backgrounds visible while content is loading.
 */
function ContentLoadError({ title, message, onRetry }) {
  return (
    <div className="app-empty-state">
      <h2>{title}</h2>
      <p>{message || 'Something went wrong loading this.'}</p>
      <button type="button" className="app-empty-state__button" onClick={onRetry}>
        Try again
      </button>
    </div>
  );
}

function ToolbarSkeleton() {
  return (
    <div className="sprint-detail">
      {/* View bar placeholder - matches ViewBar height and background */}
      <div className="toolbar-skeleton__view-bar" />
      {/* Unified toolbar placeholder - matches UnifiedToolbar height and background */}
      <div className="toolbar-skeleton__unified-toolbar" />
      {/* Loading content area */}
      <div className="toolbar-skeleton__content">
        <LogoLoader size={60} />
      </div>
    </div>
  );
}

/**
 * Main content area renderer for the App.
 * Handles rendering different views based on currentView state.
 * 
 * IMPORTANT: We use stable keys for SprintDetail to prevent unmounting during tab switches.
 * This keeps the toolbars visible while only the content area shows loading.
 */
export function MainContent({
  currentView,
  VIEWS,
  // Loading state
  isLoadingItem = false,
  itemLoadErrors = {},
  // Multi-campaign view props
  activeView,
  isLoadingViewData,
  multiCampaignData,
  // Single campaign props
  selectedSprint,
  selectedTemplate,
  // Common props
  sprints,
  preferences,
  flowViewVisibility,
  setFlowViewVisibility,
  hideViewExplainerModal,
  setHideViewExplainerModal,
  // Handlers
  handleSelectSprint,
  handleSelectTemplate,
  handleSelectView,
  handleAddSprint,
  handleUpdateSprint,
  handleRenameSprint,
  handleSprintIconChange,
  handleDeleteSprint,
  handleNestCampaignWithNotification,
  loadFullSprint,
  handleRenameView,
  handleViewIconChange,
  handleRemoveCampaignFromView,
  handleAddCampaignToView,
  removingCampaignId,
  // Template handlers
  handleUseTemplate,
  handleUpdateTemplate,
  handleRenameTemplate,
  handleTemplateIconChange,
  // Navigation
  preferredDetailView,
  pendingMissionFocus,
  clearPreferredDetailView,
  clearPendingMissionFocus,
  // Tab view mode sync
  onViewModeChange,
  // Custom view URL sync
  onCustomViewChange,
  // Modal
  openCreateSprintModal,
  openCreateNestedSprintModal,
  handleOpenInNewTab,
  sprintOrder = [],
  openWorkspaceArea,
  // Sidebar state
  sidebarCollapsed = false,
}) {
  const { activeTab } = useTabs();
  const isBlankTab = activeTab?.type === 'blank';
  const isCampaignsHome = activeTab?.type === 'campaigns';

  // Track the last valid sprint/template/view to keep toolbars visible during loading
  const lastSprintRef = useRef(selectedSprint);
  const lastTemplateRef = useRef(selectedTemplate);
  const lastMultiViewRef = useRef(null); // { activeView, multiCampaignData }
  
  // Update refs when we have valid data
  useEffect(() => {
    if (selectedSprint && Array.isArray(selectedSprint.tasks)) {
      lastSprintRef.current = selectedSprint;
    }
  }, [selectedSprint]);
  
  useEffect(() => {
    if (selectedTemplate && Array.isArray(selectedTemplate.tasks)) {
      lastTemplateRef.current = selectedTemplate;
    }
  }, [selectedTemplate]);
  
  // Track last valid multi-view data
  useEffect(() => {
    if (activeView && multiCampaignData) {
      lastMultiViewRef.current = { activeView, multiCampaignData };
    }
  }, [activeView, multiCampaignData]);

  // Stable handler — inline lambdas caused useSprintSync to re-fire on every parent render
  const handleTemplateSprintUpdate = useCallback((updated) => {
    const templateId = selectedTemplate?.id ?? lastTemplateRef.current?.id;
    if (updated.id === templateId) {
      handleUpdateTemplate(updated);
    } else {
      handleUpdateSprint(updated);
    }
  }, [selectedTemplate?.id, handleUpdateTemplate, handleUpdateSprint]);

  // User-initiated: clear a missing-parent link so the campaign becomes a normal
  // top-level campaign. Only runs when the user clicks "Keep at top level".
  const handleMakeTopLevel = useCallback((orphanSprint) => {
    if (!orphanSprint || !Array.isArray(orphanSprint.tasks)) return;
    handleUpdateSprint({
      ...orphanSprint,
      parentSprintId: null,
      parentTaskId: null,
    });
  }, [handleUpdateSprint]);

  switch (currentView) {
    case VIEWS.SPRINT_DETAIL:
      // Multi-campaign view mode
      if (activeView) {
        // Use current data if ready, otherwise use last valid data during loading
        const viewReady = activeView && multiCampaignData && !isLoadingViewData;
        const effectiveViewData = viewReady 
          ? { activeView, multiCampaignData }
          : lastMultiViewRef.current;
        
        // Only show skeleton if we have no view data at all (first load)
        if (!effectiveViewData) {
          return <ToolbarSkeleton />;
        }
        
        const { activeView: effectiveView, multiCampaignData: effectiveData } = effectiveViewData;
        
        // Use stable key - campaign count changes are handled by useSprintSync
        return (
          <SprintDetail
            key="multi-view-detail"
            sprint={null}
            allSprints={sprints}
            isTemplate={false}
            isLoadingContent={isLoadingItem || !viewReady}
            activeView={effectiveView}
            multiCampaignData={effectiveData}
            onSelectSprint={handleSelectSprint}
            onAddSprint={handleAddSprint}
            onUpdateSprint={handleUpdateSprint}
            onRenameView={handleRenameView}
            onViewIconChange={handleViewIconChange}
            onRemoveCampaignFromView={handleRemoveCampaignFromView}
            onAddCampaignToView={handleAddCampaignToView}
            removingCampaignId={removingCampaignId}
            onCreateNestedCampaign={openCreateNestedSprintModal}
            loadFullSprint={loadFullSprint}
            settings={preferences}
            initialView={effectiveView.viewMode || 'flow'}
            onViewModeChange={onViewModeChange}
            flowViewVisibility={flowViewVisibility}
            onFlowViewVisibilityChange={setFlowViewVisibility}
            hideViewExplainerModal={hideViewExplainerModal}
            onHideViewExplainerModalChange={setHideViewExplainerModal}
            sidebarCollapsed={sidebarCollapsed}
          />
        );
      }
      // Single campaign mode
      // Reuse lastSprintRef only for the SAME campaign (e.g. brief reload). Never show
      // another campaign's data while the newly selected one is still metadata-only —
      // that made "Launchpad" appear to open but keep showing Inbox/Projects.
      // Exception: if the target tab has MFV/doc open, keep SprintDetail mounted
      // (with isLoadingContent) so the doc sheet doesn't unmount mid-switch.
      const sprintReady = selectedSprint && Array.isArray(selectedSprint.tasks);
      const lastSprint = lastSprintRef.current;
      const canReuseLastSprint =
        !!lastSprint && !!selectedSprint && lastSprint.id === selectedSprint.id;
      const tabKeepsOverlay = !!(
        activeTab?.openMissionId ||
        (Array.isArray(activeTab?.missionDocStack) && activeTab.missionDocStack.length > 0)
      );
      const sprintLoadError = selectedSprint?.id ? itemLoadErrors[selectedSprint.id] : null;
      const sprintGate = detailContentGate({
        ready: sprintReady,
        loadError: sprintLoadError,
        canReuseLast: canReuseLastSprint,
        keepOverlay: tabKeepsOverlay,
        hasLast: !!lastSprint,
        isLoadingItem,
      });

      if (sprintGate === 'error') {
        return (
          <ContentLoadError
            title="Couldn't load this tab"
            message={sprintLoadError}
            onRetry={() => loadFullSprint(selectedSprint.id)}
          />
        );
      }

      const effectiveSprint = sprintReady
        ? selectedSprint
        : (canReuseLastSprint || tabKeepsOverlay ? lastSprint : null);

      // Skeleton while switching to a campaign that isn't fully loaded yet
      if (sprintGate === 'skeleton' || !effectiveSprint) {
        return <ToolbarSkeleton />;
      }

      const showOrphanBanner = isTrulyOrphaned(effectiveSprint, sprints);

      return (
        <>
          {showOrphanBanner && (
            <OrphanWarningBanner
              sprint={effectiveSprint}
              allSprints={sprints}
              onMakeTopLevel={handleMakeTopLevel}
              onDelete={handleDeleteSprint}
            />
          )}
          <SprintDetail
            key="sprint-detail"
            sprint={effectiveSprint}
          allSprints={sprints}
          isTemplate={false}
          isLoadingContent={sprintGate === 'loading'}
          onSelectSprint={handleSelectSprint}
          onAddSprint={handleAddSprint}
          onUpdateSprint={handleUpdateSprint}
          onRenameSprint={handleRenameSprint}
          onSprintIconChange={handleSprintIconChange}
          onTrashSprint={handleDeleteSprint}
          onNestCampaign={handleNestCampaignWithNotification}
          onCreateNestedCampaign={openCreateNestedSprintModal}
          loadFullSprint={loadFullSprint}
          settings={preferences}
          initialView={preferredDetailView}
          initialMissionFocus={pendingMissionFocus}
          onViewChanged={clearPreferredDetailView}
          onMissionFocused={clearPendingMissionFocus}
          onViewModeChange={onViewModeChange}
          onCustomViewChange={onCustomViewChange}
          flowViewVisibility={flowViewVisibility}
          onFlowViewVisibilityChange={setFlowViewVisibility}
          hideViewExplainerModal={hideViewExplainerModal}
            onHideViewExplainerModalChange={setHideViewExplainerModal}
            sidebarCollapsed={sidebarCollapsed}
          />
        </>
      );

    case VIEWS.TEMPLATE_DETAIL:
      // Same rule as campaigns: never reuse a different template's cached data.
      // Keep mounted when the tab has MFV/doc open so the sheet doesn't flash.
      const templateReady = selectedTemplate && Array.isArray(selectedTemplate.tasks);
      const lastTemplate = lastTemplateRef.current;
      const canReuseLastTemplate =
        !!lastTemplate && !!selectedTemplate && lastTemplate.id === selectedTemplate.id;
      const templateTabKeepsOverlay = !!(
        activeTab?.openMissionId ||
        (Array.isArray(activeTab?.missionDocStack) && activeTab.missionDocStack.length > 0)
      );
      const templateLoadError = selectedTemplate?.id ? itemLoadErrors[selectedTemplate.id] : null;
      const templateGate = detailContentGate({
        ready: templateReady,
        loadError: templateLoadError,
        canReuseLast: canReuseLastTemplate,
        keepOverlay: templateTabKeepsOverlay,
        hasLast: !!lastTemplate,
        isLoadingItem,
      });

      if (templateGate === 'error') {
        return (
          <ContentLoadError
            title="Couldn't load this tab"
            message={templateLoadError}
            onRetry={() => loadFullSprint(selectedTemplate.id)}
          />
        );
      }

      const effectiveTemplate = templateReady
        ? selectedTemplate
        : (canReuseLastTemplate || templateTabKeepsOverlay ? lastTemplate : null);

      if (templateGate === 'skeleton' || !effectiveTemplate) {
        return <ToolbarSkeleton />;
      }
      
      return (
        <SprintDetail
          key="template-detail"
          sprint={effectiveTemplate}
          allSprints={sprints}
          isTemplate={true}
          isLoadingContent={templateGate === 'loading'}
          onUseTemplate={handleUseTemplate}
          onSelectSprint={handleSelectSprint}
          onAddSprint={handleAddSprint}
          onUpdateSprint={handleTemplateSprintUpdate}
          onRenameSprint={handleRenameTemplate}
          onSprintIconChange={handleTemplateIconChange}
          onTrashSprint={handleDeleteSprint}
          onNestCampaign={handleNestCampaignWithNotification}
          onCreateNestedCampaign={openCreateNestedSprintModal}
          loadFullSprint={loadFullSprint}
          settings={preferences}
          initialView={preferredDetailView}
          onViewChanged={clearPreferredDetailView}
          onViewModeChange={onViewModeChange}
          onCustomViewChange={onCustomViewChange}
          flowViewVisibility={flowViewVisibility}
          onFlowViewVisibilityChange={setFlowViewVisibility}
          hideViewExplainerModal={hideViewExplainerModal}
          onHideViewExplainerModalChange={setHideViewExplainerModal}
          sidebarCollapsed={sidebarCollapsed}
        />
      );

    case VIEWS.DOCS:
      return <DocsArea />;

    case VIEWS.DASHBOARDS:
      return (
        <div className="app-empty-state">
          <h2>Dashboards</h2>
          <p>A lens across campaigns — stats, what’s moving, what’s stuck. Coming next.</p>
        </div>
      );

    default:
      if (isCampaignsHome) {
        return (
          <CampaignsArea
            sprints={sprints}
            sprintOrder={sprintOrder}
            onSelectSprint={handleSelectSprint}
            onOpenInNewTab={handleOpenInNewTab}
            onRenameSprint={handleRenameSprint}
            onTrashSprint={handleDeleteSprint}
            openCreateSprintModal={openCreateSprintModal}
            openCreateNestedSprintModal={openCreateNestedSprintModal}
          />
        );
      }

      if (isBlankTab) {
        return (
          <NewTabPage
            sprints={sprints}
            onSelectSprint={handleSelectSprint}
            onSelectTemplate={handleSelectTemplate}
            onSelectView={handleSelectView}
            openCreateSprintModal={openCreateSprintModal}
          />
        );
      }

      // Check if user has any campaigns/templates to show appropriate message
      const hasItems = sprints && sprints.some(s => !s.deletedAt && !s.isTemplate);
      return (
        <div className="app-empty-state">
          <LogoLoader size={80} />
          {hasItems ? (
            <>
              <h2>Select a Campaign or View</h2>
              <p>Choose a campaign, template, or multi view from the sidebar to get started.</p>
            </>
          ) : (
            <>
              <h2>Welcome to Fokus</h2>
              <p>Create your first campaign to get started!</p>
              <button className="app-empty-state__button" onClick={() => openCreateSprintModal()}>
                + New Campaign
              </button>
            </>
          )}
        </div>
      );
  }
}
